import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BugReportIcon from "@mui/icons-material/BugReport";
import LinkIcon from "@mui/icons-material/Link";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { projectAPI, defectAPI } from "../../api";
import { useTheme, useMediaQuery } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BASE_URL = API_URL.endsWith("/api") ? API_URL.slice(0, -4) : API_URL;
const UPLOADS_URL = `${BASE_URL}/uploads`;

const SEVERITY_COLORS = {
  blocker: { bg: "#88004F18", color: "#880E4F" },
  critical: { bg: "#C6282818", color: "#C62828" },
  major: { bg: "#E6510018", color: "#E65100" },
  minor: { bg: "#F57C0018", color: "#F57C00" },
  trivial: { bg: "#82771718", color: "#827717" },
};
const STATUS_COLORS = {
  open: { bg: "#C6282818", color: "#C62828" },
  in_progress: { bg: "#E6510018", color: "#E65100" },
  fixed: { bg: "#1565C018", color: "#1565C0" },
  verified: { bg: "#2E7D3218", color: "#2E7D32" },
  closed: { bg: "#42424218", color: "#424242" },
  reopen: { bg: "#88004F18", color: "#880E4F" },
};
const TYPE_COLORS = {
  Defect: { bg: "#C6282818", color: "#C62828" },
  Finding: { bg: "#1565C018", color: "#1565C0" },
};

const SEVERITIES = ["blocker", "critical", "major", "minor", "trivial"];
const PRIORITIES = ["p1", "p2", "p3", "p4"];
const ENVIRONMENTS = ["dev", "qa", "staging", "production"];
const DEFECT_TYPES = ["Defect", "Finding"];
const ALL_STATUSES = [
  "open",
  "in_progress",
  "fixed",
  "verified",
  "closed",
  "reopen",
];

const emptyForm = {
  title: "",
  url: "",
  description: "",
  actual_result: "",
  expected_result: "",
  defect_type: "Defect",
  severity: "major",
  priority: "p2",
  environment: "qa",
  status: "open",
  assigned_to: "",
  remarks: "",
  qa_remarks: "",
  attachment: null,
};

// API helper with file support
const defectRequest = async (method, url, data, token) => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (k === "attachment" && v) formData.append("attachment", v);
    else if (v !== null && v !== undefined && v !== "") formData.append(k, v);
  });
  return axios({
    method,
    url: `${API_URL}${url}`,
    data: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });
};

function AttachmentHistory({ attachments, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (!attachments || attachments.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No files uploaded yet
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {attachments.map((a) => {
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(a.filename);
        return (
          <Box
            key={a.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            {isImage ? (
              <ImageIcon sx={{ color: "text.disabled", fontSize: 20 }} />
            ) : (
              <AttachFileIcon sx={{ color: "text.disabled", fontSize: 20 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {a.original_name || a.filename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Uploaded by {a.uploader?.name || "Unknown"}
                {a.created_at
                  ? ` · ${a.created_at.replace("T", " ").split(".")[0]}`
                  : ""}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() =>
                window.open(`${UPLOADS_URL}/${a.filename}`, "_blank")
              }
            >
              View
            </Button>
          </Box>
        );
      })}
    </Stack>
  );
}

export default function DefectsPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isQA = ["qa_team", "admin"].includes(user?.role);
  const isDeveloper = user?.role === "team_member";
  const token = localStorage.getItem("token");

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [defects, setDefects] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editDefect, setEditDefect] = useState(null);
  const [viewDefect, setViewDefect] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    severity: "",
    defect_type: "",
  });
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  useEffect(() => {
    projectAPI.getAll().then((res) => {
      const ps = res.data.projects || [];
      setProjects(ps);
      if (ps.length > 0) setSelectedProject(ps[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    loadDefects();
    projectAPI
      .getMembers(selectedProject)
      .then((res) => setProjectMembers(res.data))
      .catch(() => {});
  }, [selectedProject, filters]);

  const loadDefects = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.severity) params.append("severity", filters.severity);
    axios
      .get(`${API_URL}/projects/${selectedProject}/defects?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .then((res) => setDefects(res.data))
      .catch(() => setError("Failed to load defects"))
      .finally(() => setLoading(false));
  };

  const handleSubmit = async () => {
    setError("");
    if (!isDeveloper && !form.title) return setError("Title is required");
    if (!isDeveloper && !editDefect && !form.attachment)
      return setError("File attachment is required");
    try {
      if (editDefect) {
        await defectRequest(
          "put",
          `/projects/${selectedProject}/defects/${editDefect.id}`,
          form,
          token,
        );
        setSuccess("Defect updated successfully");
      } else {
        await defectRequest(
          "post",
          `/projects/${selectedProject}/defects`,
          form,
          token,
        );
        setSuccess("Defect logged successfully");
      }
      setOpenForm(false);
      setEditDefect(null);
      setForm(emptyForm);
      loadDefects();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save defect");
    }
  };

  const loadAttachments = (defectId) => {
    if (!selectedProject) return;
    setAttachmentsLoading(true);
    defectAPI
      .getAttachments(selectedProject, defectId)
      .then((res) => setAttachments(res.data))
      .catch(() => setAttachments([]))
      .finally(() => setAttachmentsLoading(false));
  };

  const openEdit = (defect) => {
    setEditDefect(defect);
    loadAttachments(defect.id);
    setForm({
      title: defect.title || "",
      url: defect.url || "",
      description: defect.description || "",
      actual_result: defect.actual_result || "",
      expected_result: defect.expected_result || "",
      defect_type: defect.defect_type || "Defect",
      severity: defect.severity || "major",
      priority: defect.priority || "p2",
      environment: defect.environment || "qa",
      status: defect.status || "open",
      assigned_to: defect.assigned_to || "",
      remarks: defect.remarks || "",
      qa_remarks: defect.qa_remarks || "",
      attachment: null,
    });
    setOpenForm(true);
  };

  const getAllowedStatuses = () =>
    isDeveloper ? ALL_STATUSES.filter((s) => s !== "closed") : ALL_STATUSES;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", sm: "2rem" } }}
          >
            Defects & Bugs
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            Track and manage project defects
          </Typography>
        </Box>
        {isQA && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            onClick={() => {
              setEditDefect(null);
              setForm(emptyForm);
              setOpenForm(true);
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              Log{" "}
            </Box>
            Defect
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: "16px !important" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                label="Project"
                value={selectedProject}
                size="small"
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Type"
                value={filters.defect_type}
                size="small"
                onChange={(e) =>
                  setFilters({ ...filters, defect_type: e.target.value })
                }
              >
                <MenuItem value="">All Types</MenuItem>
                {DEFECT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={filters.status}
                size="small"
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <MenuItem value="">All Status</MenuItem>
                {ALL_STATUSES.map((s) => (
                  <MenuItem
                    key={s}
                    value={s}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {s.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Severity"
                value={filters.severity}
                size="small"
                onChange={(e) =>
                  setFilters({ ...filters, severity: e.target.value })
                }
              >
                <MenuItem value="">All Severity</MenuItem>
                {SEVERITIES.map((s) => (
                  <MenuItem
                    key={s}
                    value={s}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                sx={{ height: 40 }}
                onClick={() =>
                  setFilters({ status: "", severity: "", defect_type: "" })
                }
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-head": {
                      bgcolor: "#F4F7FB",
                      fontWeight: 700,
                      color: "#1E3A5F",
                      fontSize: "0.75rem",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", sm: "table-cell" } }}
                    width={50}
                  >
                    URL
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", sm: "table-cell" } }}
                    width={80}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", md: "table-cell" } }}
                    width={90}
                  >
                    Severity
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", md: "table-cell" } }}
                    width={70}
                  >
                    Priority
                  </TableCell>
                  <TableCell width={90}>Status</TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", lg: "table-cell" } }}
                    width={90}
                  >
                    Environment
                  </TableCell>
                  <TableCell width={100}>Reporter</TableCell>
                  <TableCell width={100}>Assignee</TableCell>
                  <TableCell width={80} align="center">
                    Attach
                  </TableCell>
                  <TableCell width={70} align="center">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defects.map((d) => (
                  <TableRow
                    key={d.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#F8FAFD" },
                    }}
                    onClick={() => {
                      setViewDefect(d);
                      loadAttachments(d.id);
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                      }}
                    >
                      #{d.id}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: 120, sm: 170 },
                        }}
                      >
                        {d.title}
                      </Typography>
                      <Box
                        sx={{
                          display: { xs: "flex", sm: "none" },
                          gap: 0.5,
                          mt: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={d.defect_type || "Defect"}
                          size="small"
                          sx={{
                            bgcolor: TYPE_COLORS[d.defect_type || "Defect"]?.bg,
                            color:
                              TYPE_COLORS[d.defect_type || "Defect"]?.color,
                            fontWeight: 700,
                            fontSize: "0.58rem",
                            height: 18,
                          }}
                        />
                        <Chip
                          label={d.severity}
                          size="small"
                          sx={{
                            bgcolor: SEVERITY_COLORS[d.severity]?.bg,
                            color: SEVERITY_COLORS[d.severity]?.color,
                            fontWeight: 700,
                            fontSize: "0.58rem",
                            height: 18,
                            textTransform: "capitalize",
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      {d.url ? (
                        <Tooltip title={d.url}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(d.url, "_blank");
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Chip
                        label={d.defect_type || "Defect"}
                        size="small"
                        sx={{
                          bgcolor: TYPE_COLORS[d.defect_type || "Defect"]?.bg,
                          color: TYPE_COLORS[d.defect_type || "Defect"]?.color,
                          fontWeight: 700,
                          fontSize: "0.62rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      <Chip
                        label={d.severity}
                        size="small"
                        sx={{
                          bgcolor: SEVERITY_COLORS[d.severity]?.bg,
                          color: SEVERITY_COLORS[d.severity]?.color,
                          fontWeight: 700,
                          fontSize: "0.62rem",
                          height: 22,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      <Chip
                        label={d.priority?.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.62rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={d.status?.replace("_", " ")}
                        size="small"
                        sx={{
                          bgcolor: STATUS_COLORS[d.status]?.bg,
                          color: STATUS_COLORS[d.status]?.color,
                          fontWeight: 700,
                          fontSize: "0.62rem",
                          height: 22,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", lg: "table-cell" } }}
                    >
                      <Chip
                        label={d.environment}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.62rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            fontSize: "0.6rem",
                            bgcolor: "primary.main",
                            flexShrink: 0,
                          }}
                        >
                          {d.reporter?.name?.charAt(0)}
                        </Avatar>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 70,
                          }}
                        >
                          {d.reporter?.name || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {d.assignee ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 22,
                              height: 22,
                              fontSize: "0.6rem",
                              bgcolor: "secondary.main",
                              flexShrink: 0,
                            }}
                          >
                            {d.assignee?.name?.charAt(0)}
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 70,
                            }}
                          >
                            {d.assignee?.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {d.attachment_path ? (
                        <Tooltip title="View attachment">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `${UPLOADS_URL}/${d.attachment_path}`,
                                "_blank",
                              );
                            }}
                            sx={{
                              bgcolor: "#1565C010",
                              "&:hover": { bgcolor: "#1565C020" },
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEdit(d)}
                          sx={{
                            bgcolor: "#1E3A5F10",
                            "&:hover": { bgcolor: "#1E3A5F20" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {defects.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      sx={{
                        textAlign: "center",
                        py: 8,
                        color: "text.secondary",
                      }}
                    >
                      <BugReportIcon
                        sx={{
                          fontSize: 48,
                          mb: 1,
                          opacity: 0.2,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No defects logged yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Log / Edit Defect Dialog */}
      <Dialog
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditDefect(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 2 }, m: { xs: 0, sm: 2 } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {editDefect ? `Edit Defect #${editDefect.id}` : "Log New Defect"}
            {isDeveloper && editDefect && (
              <Chip
                label="Developer View"
                size="small"
                color="info"
                sx={{ fontWeight: 700, fontSize: "0.65rem" }}
              />
            )}
          </Box>
          <IconButton
            onClick={() => {
              setOpenForm(false);
              setEditDefect(null);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* ── Developer: can only update status + remarks ── */}
          {isDeveloper && editDefect ? (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {/* Read-only defect info */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                >
                  Defect Details (Read Only)
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                  {editDefect.title}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1 }}
                  flexWrap="wrap"
                  gap={0.5}
                >
                  <Chip
                    label={editDefect.defect_type || "Defect"}
                    size="small"
                    sx={{
                      bgcolor:
                        TYPE_COLORS[editDefect.defect_type || "Defect"]?.bg,
                      color:
                        TYPE_COLORS[editDefect.defect_type || "Defect"]?.color,
                      fontWeight: 700,
                      fontSize: "0.62rem",
                    }}
                  />
                  <Chip
                    label={editDefect.severity}
                    size="small"
                    sx={{
                      bgcolor: SEVERITY_COLORS[editDefect.severity]?.bg,
                      color: SEVERITY_COLORS[editDefect.severity]?.color,
                      fontWeight: 700,
                      fontSize: "0.62rem",
                      textTransform: "capitalize",
                    }}
                  />
                  <Chip
                    label={editDefect.priority?.toUpperCase()}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: "0.62rem" }}
                  />
                  <Chip
                    label={editDefect.environment}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.62rem" }}
                  />
                </Stack>
                {editDefect.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {editDefect.description}
                  </Typography>
                )}
              </Box>

              {/* Actual & Expected - read only */}
              {(editDefect.actual_result || editDefect.expected_result) && (
                <Grid container spacing={2}>
                  {editDefect.actual_result && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "#FFF3E0",
                          borderRadius: 2,
                          borderLeft: "4px solid #E65100",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#E65100",
                            textTransform: "uppercase",
                            fontSize: "0.62rem",
                          }}
                        >
                          Actual Result
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {editDefect.actual_result}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {editDefect.expected_result && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "#E8F5E9",
                          borderRadius: 2,
                          borderLeft: "4px solid #2E7D32",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#2E7D32",
                            textTransform: "uppercase",
                            fontSize: "0.62rem",
                          }}
                        >
                          Expected Result
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {editDefect.expected_result}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}

              <Divider />

              {/* Attachment - show if exists */}
              {editDefect?.attachment_path && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Latest Attachment
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {/\.(jpg|jpeg|png|gif|webp)$/i.test(
                      editDefect.attachment_path,
                    ) ? (
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#f5f5f5",
                          textAlign: "center",
                        }}
                      >
                        <img
                          src={`${UPLOADS_URL}/${editDefect.attachment_path}`}
                          alt="defect attachment"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 250,
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(
                              `${UPLOADS_URL}/${editDefect.attachment_path}`,
                              "_blank",
                            )
                          }
                          onLoad={() =>
                            console.log(
                              "Image loaded:",
                              `${UPLOADS_URL}/${editDefect.attachment_path}`,
                            )
                          }
                          onError={(e) => {
                            console.log(
                              "Image failed:",
                              `${UPLOADS_URL}/${editDefect.attachment_path}`,
                            );
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <Box
                          sx={{
                            display: "none",
                            flexDirection: "column",
                            alignItems: "center",
                            py: 3,
                            color: "text.secondary",
                          }}
                        >
                          <ImageIcon
                            sx={{ fontSize: 40, opacity: 0.3, mb: 1 }}
                          />
                          <Typography variant="caption">
                            Image not found
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() =>
                              window.open(
                                `${UPLOADS_URL}/${editDefect.attachment_path}`,
                                "_blank",
                              )
                            }
                          >
                            Try opening directly
                          </Button>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            p: 0.5,
                            color: "text.secondary",
                            bgcolor: "background.paper",
                          }}
                        >
                          Click to open full size
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        onClick={() =>
                          window.open(
                            `${UPLOADS_URL}/${editDefect.attachment_path}`,
                            "_blank",
                          )
                        }
                      >
                        View Attachment — {editDefect.attachment_path}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              <Divider />

              {/* QA Remarks - read only for developer */}
              {editDefect?.qa_remarks && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#E3F2FD",
                    borderRadius: 2,
                    borderLeft: "4px solid #1565C0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#1565C0",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    🔍 QA Remarks
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {editDefect.qa_remarks}
                  </Typography>
                </Box>
              )}

              {/* Editable: Status only (not closed) */}
              <TextField
                fullWidth
                select
                label="Update Status"
                size="small"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {ALL_STATUSES.filter((s) => s !== "closed").map((s) => (
                  <MenuItem
                    key={s}
                    value={s}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {s.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>

              {/* Remarks */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks / Developer Notes"
                size="small"
                placeholder="Add your fix details, notes or comments here..."
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />

              {/* Upload a new attachment (e.g. proof of fix) — replaces the existing one */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    size="small"
                  >
                    {form.attachment ? form.attachment.name : "Upload File"}
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.log,.txt"
                      onChange={(e) =>
                        setForm({ ...form, attachment: e.target.files[0] })
                      }
                    />
                  </Button>
                  {form.attachment && (
                    <Typography
                      variant="caption"
                      sx={{ color: "success.main", fontWeight: 600 }}
                    >
                      ✓ {form.attachment.name}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                >
                  Every upload is kept — this doesn't replace earlier files
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Attachment History
                </Typography>
                <AttachmentHistory
                  attachments={attachments}
                  loading={attachmentsLoading}
                />
              </Box>
            </Stack>
          ) : (
            /* ── QA / Admin: full form ── */
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                required
                label="Title"
                size="small"
                placeholder="Short summary of the defect"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />

              <TextField
                fullWidth
                label="URL"
                size="small"
                placeholder="https://example.com/page-with-defect"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <LinkIcon
                      sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                    />
                  ),
                }}
              />

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Type"
                    size="small"
                    value={form.defect_type}
                    onChange={(e) =>
                      setForm({ ...form, defect_type: e.target.value })
                    }
                  >
                    {DEFECT_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Severity"
                    size="small"
                    value={form.severity}
                    onChange={(e) =>
                      setForm({ ...form, severity: e.target.value })
                    }
                  >
                    {SEVERITIES.map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        sx={{ textTransform: "capitalize" }}
                      >
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Priority"
                    size="small"
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value })
                    }
                  >
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Environment"
                    size="small"
                    value={form.environment}
                    onChange={(e) =>
                      setForm({ ...form, environment: e.target.value })
                    }
                  >
                    {ENVIRONMENTS.map((e) => (
                      <MenuItem key={e} value={e}>
                        {e}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    size="small"
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value })
                    }
                  >
                    {ALL_STATUSES.map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        sx={{ textTransform: "capitalize" }}
                      >
                        {s.replace("_", " ")}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Assign To"
                    size="small"
                    value={form.assigned_to}
                    onChange={(e) =>
                      setForm({ ...form, assigned_to: e.target.value })
                    }
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {projectMembers.map((m) => (
                      <MenuItem key={m.user_id} value={m.user_id}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 20,
                              height: 20,
                              fontSize: "0.6rem",
                              bgcolor: "primary.main",
                            }}
                          >
                            {m.member?.name?.charAt(0)}
                          </Avatar>
                          {m.member?.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Developer Remarks - read only for QA */}
              {editDefect?.remarks && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#EDE7F6",
                    borderRadius: 2,
                    borderLeft: "4px solid #7B1FA2",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#7B1FA2",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    💬 Developer Remarks
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {editDefect.remarks}
                  </Typography>
                </Box>
              )}

              {/* QA Remarks */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="QA Remarks"
                size="small"
                placeholder="Add QA notes, test observations, or closure comments..."
                value={form.qa_remarks}
                onChange={(e) =>
                  setForm({ ...form, qa_remarks: e.target.value })
                }
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  size="small"
                >
                  {form.attachment ? form.attachment.name : "Upload File *"}
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.log,.txt"
                    onChange={(e) =>
                      setForm({ ...form, attachment: e.target.files[0] })
                    }
                  />
                </Button>
                {form.attachment && (
                  <Typography
                    variant="caption"
                    sx={{ color: "success.main", fontWeight: 600 }}
                  >
                    ✓ {form.attachment.name}
                  </Typography>
                )}
                {editDefect?.attachment_path && !form.attachment && (
                  <Chip
                    label="Existing attachment"
                    size="small"
                    icon={<ImageIcon />}
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        `${UPLOADS_URL}/${editDefect.attachment_path}`,
                        "_blank",
                      )
                    }
                  />
                )}
              </Box>

              {editDefect && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Attachment History
                  </Typography>
                  <AttachmentHistory
                    attachments={attachments}
                    loading={attachmentsLoading}
                  />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenForm(false);
              setEditDefect(null);
            }}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              (!form.title && !isDeveloper) ||
              (!editDefect && !form.attachment && !isDeveloper)
            }
            sx={{ flex: 2 }}
          >
            {editDefect ? "Save Changes" : "Log Defect"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Defect Detail */}
      {viewDefect && (
        <Dialog
          open={!!viewDefect}
          onClose={() => setViewDefect(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <BugReportIcon color="error" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {viewDefect.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{viewDefect.id} · Reported by {viewDefect.reporter?.name} ·{" "}
                  {viewDefect.created_at?.split("T")[0]}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5}>
              {/* Badges */}
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Chip
                  label={viewDefect.defect_type || "Defect"}
                  size="small"
                  sx={{
                    bgcolor:
                      TYPE_COLORS[viewDefect.defect_type || "Defect"]?.bg,
                    color:
                      TYPE_COLORS[viewDefect.defect_type || "Defect"]?.color,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  label={viewDefect.severity}
                  size="small"
                  sx={{
                    bgcolor: SEVERITY_COLORS[viewDefect.severity]?.bg,
                    color: SEVERITY_COLORS[viewDefect.severity]?.color,
                    fontWeight: 700,
                    textTransform: "capitalize",
                  }}
                />
                <Chip
                  label={viewDefect.priority?.toUpperCase()}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  label={viewDefect.status?.replace("_", " ")}
                  size="small"
                  sx={{
                    bgcolor: STATUS_COLORS[viewDefect.status]?.bg,
                    color: STATUS_COLORS[viewDefect.status]?.color,
                    fontWeight: 700,
                    textTransform: "capitalize",
                  }}
                />
                <Chip
                  label={viewDefect.environment}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Divider />

              {/* URL */}
              {viewDefect.url && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    URL
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        wordBreak: "break-all",
                      }}
                      onClick={() => window.open(viewDefect.url, "_blank")}
                    >
                      {viewDefect.url}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Description */}
              {viewDefect.description && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {viewDefect.description}
                  </Typography>
                </Box>
              )}

              {/* Actual & Expected */}
              <Grid container spacing={2}>
                {viewDefect.actual_result && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#FFF3E0",
                        borderRadius: 2,
                        borderLeft: "4px solid #E65100",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#E65100",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Actual Result
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                      >
                        {viewDefect.actual_result}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {viewDefect.expected_result && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#E8F5E9",
                        borderRadius: 2,
                        borderLeft: "4px solid #2E7D32",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#2E7D32",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Expected Result
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                      >
                        {viewDefect.expected_result}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Assignee
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {viewDefect.assignee?.name || "Unassigned"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Attachment
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {viewDefect.attachment_path ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={() =>
                          window.open(
                            `${UPLOADS_URL}/${viewDefect.attachment_path}`,
                            "_blank",
                          )
                        }
                      >
                        View Attachment
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        None
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {viewDefect.remarks && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#EDE7F6",
                        borderRadius: 2,
                        borderLeft: "4px solid #7B1FA2",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#7B1FA2",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        💬 Developer Remarks
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                      >
                        {viewDefect.remarks}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {viewDefect.qa_remarks && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#E3F2FD",
                        borderRadius: 2,
                        borderLeft: "4px solid #1565C0",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#1565C0",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        🔍 QA Remarks
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                      >
                        {viewDefect.qa_remarks}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Attachment Preview */}
              {viewDefect.attachment_path && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    {/\.(jpg|jpeg|png|gif|webp)$/i.test(
                      viewDefect.attachment_path,
                    )
                      ? "Screenshot"
                      : "Attachment"}
                  </Typography>
                  {/\.(jpg|jpeg|png|gif|webp)$/i.test(
                    viewDefect.attachment_path,
                  ) ? (
                    <Box
                      sx={{
                        mt: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "#f5f5f5",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={`${UPLOADS_URL}/${viewDefect.attachment_path}`}
                        alt="defect screenshot"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 300,
                          objectFit: "contain",
                          display: "block",
                          margin: "0 auto",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          window.open(
                            `${UPLOADS_URL}/${viewDefect.attachment_path}`,
                            "_blank",
                          )
                        }
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          p: 0.5,
                          color: "text.secondary",
                          bgcolor: "background.paper",
                        }}
                      >
                        Click to open full size
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        onClick={() =>
                          window.open(
                            `${UPLOADS_URL}/${viewDefect.attachment_path}`,
                            "_blank",
                          )
                        }
                      >
                        Open Attachment
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Attachment History
                </Typography>
                <AttachmentHistory
                  attachments={attachments}
                  loading={attachmentsLoading}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="outlined" onClick={() => setViewDefect(null)}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setViewDefect(null);
                openEdit(viewDefect);
              }}
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}